import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  cardClass,
  cardHeaderClass,
  cardTitleClass,
  cardBodyClass,
  formLabelClass,
  formControlClass,
  btnPrimaryClass,
} from "./dashboardStyles";
import {
  NotificationSettingsStatus,
  ReadOnlyBadge,
} from "./NotificationPreferencesPanel";
import { getTimeZoneOptions } from "../../constants/notifications";

/**
 * The Language & time zone tab of the Settings page.
 *
 * The language is the one the *business's* customers read their booking emails
 * in (`Organization.Locale`), not this browser's interface language. The time
 * zone lives here rather than on the organization profile because notification
 * timing is the only thing it decides: it is what "24 hours before" is measured
 * in.
 *
 * Both are fields of the notification settings record, so this tab edits the
 * same `useNotificationSettings` form as the Notifications tab and its save
 * sends both tabs' changes.
 */
const LanguagePreferencesPanel = ({ settings, canEdit }) => {
  const { t } = useTranslation();
  const { form, setField, availableLocales, loading, saving, save } = settings;

  const handleSubmit = (event) => {
    event.preventDefault();
    save();
  };

  const timeZoneOptions = getTimeZoneOptions();

  return (
    <div className={cardClass}>
      <div className={cardHeaderClass}>
        <h3 className={`${cardTitleClass} flex items-center gap-2`}>
          <i className="bi bi-globe2 text-ink-500" aria-hidden="true" />
          {t("Dashboard.Notifications.localeChannel")}
        </h3>
        {!canEdit && !loading && form && <ReadOnlyBadge />}
      </div>

      <div className={cardBodyClass}>
        <NotificationSettingsStatus settings={settings} />
        {!loading && form && (
          <form onSubmit={handleSubmit} className="flex flex-col gap-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="min-w-0">
                <label className={formLabelClass} htmlFor="locale">
                  {t("Dashboard.Notifications.locale")}
                </label>
                <select
                  id="locale"
                  name="locale"
                  className={formControlClass}
                  value={form.locale}
                  disabled={!canEdit}
                  onChange={(event) => setField("locale", event.target.value)}
                >
                  {availableLocales.map((code) => (
                    <option key={code} value={code}>
                      {t(`Dashboard.Notifications.locales.${code}`)}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-ink-500">
                  {t("Dashboard.Notifications.localeHelp")}
                </p>
              </div>

              <div className="min-w-0">
                <label className={formLabelClass} htmlFor="timeZoneId">
                  {t("Dashboard.Notifications.timeZone")}
                </label>
                <select
                  id="timeZoneId"
                  name="timeZoneId"
                  className={formControlClass}
                  value={form.timeZoneId}
                  disabled={!canEdit}
                  onChange={(event) => setField("timeZoneId", event.target.value)}
                >
                  {/* A zone stored before it left this engine's list would
                      otherwise vanish from the picker and be silently
                      reassigned on the next save. */}
                  {!timeZoneOptions.some((zone) => zone.id === form.timeZoneId) && (
                    <option value={form.timeZoneId}>{form.timeZoneId}</option>
                  )}
                  {timeZoneOptions.map((zone) => (
                    <option key={zone.id} value={zone.id}>
                      {zone.label}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-ink-500">
                  {t("Dashboard.Notifications.timeZoneHelp")}
                </p>
              </div>
            </div>

            {canEdit && (
              <div className="flex justify-end border-t border-ink-100 pt-5">
                <button
                  type="submit"
                  className={`${btnPrimaryClass} min-h-11 max-sm:w-full`}
                  disabled={saving}
                >
                  {saving
                    ? t("Dashboard.Notifications.saving")
                    : t("Dashboard.Notifications.save")}
                </button>
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

LanguagePreferencesPanel.propTypes = {
  settings: PropTypes.shape({
    form: PropTypes.object,
    setField: PropTypes.func.isRequired,
    availableLocales: PropTypes.arrayOf(PropTypes.string).isRequired,
    loading: PropTypes.bool,
    saving: PropTypes.bool,
    save: PropTypes.func.isRequired,
  }).isRequired,
  canEdit: PropTypes.bool,
};

export default LanguagePreferencesPanel;
